import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../lib/api";
import { ChartLineUp, Clock } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(Math.round(n || 0));
const fmtCompact = (n) => {
  if (!n) return "0";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
};

export default function HistoryChart({ days = 30 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/character/history", { params: { days } })
      .then(r => {
        // Format for recharts
        const rows = r.data.map(s => ({
          t: new Date(s.timestamp).getTime(),
          label: new Date(s.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
          cash: s.cash,
          bank: s.bank,
          wealth: s.total_wealth,
          playtime_hours: Math.round((s.playtime_minutes || 0) / 60 * 10) / 10,
        }));
        setData(rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="border border-tactical-border bg-tactical-surface" data-testid="history-chart">
      <div className="px-5 py-4 border-b border-tactical-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ChartLineUp size={18} className="text-volt" weight="duotone" />
          <div>
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Verlauf letzte {days} Tage</div>
            <div className="font-display font-bold text-lg">Vermögen & Playtime</div>
          </div>
        </div>
        <div className="font-mono text-xs text-white/40">{data.length} Datenpunkte</div>
      </div>

      {loading && (
        <div className="p-12 text-center font-mono text-white/40 text-sm">Lade Verlauf...</div>
      )}
      {!loading && data.length < 2 && (
        <div className="p-12 text-center font-mono text-white/50 text-sm">
          <Clock size={22} className="mx-auto mb-2 text-white/30" />
          Noch nicht genug Daten. Die App sammelt automatisch alle 15 Minuten Snapshots.<br/>
          Komm in ein paar Stunden wieder — der Chart baut sich dann auf.
        </div>
      )}
      {!loading && data.length >= 2 && (
        <div className="p-2 md:p-4">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#222" />
              <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#333" />
              <YAxis yAxisId="left" tick={{ fill: "#888", fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#333" tickFormatter={fmtCompact} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#888", fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#333" />
              <Tooltip
                contentStyle={{ background: "#0a0a0a", border: "1px solid #222", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                labelStyle={{ color: "#aaa" }}
                formatter={(v, name) => name === "Playtime (h)" ? [`${v} h`, name] : [`$${fmt(v)}`, name]}
              />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }} />
              <Line yAxisId="left" type="monotone" dataKey="cash" name="Cash" stroke="#34C759" strokeWidth={1.5} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="bank" name="Bank" stroke="#007AFF" strokeWidth={1.5} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="wealth" name="Vermögen" stroke="#ffffff" strokeWidth={2} dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="playtime_hours" name="Playtime (h)" stroke="#FF9500" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
